import React from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MessageSquareIcon, ShieldCheckIcon, BadgeCheckIcon, MapPinIcon } from 'lucide-react';
import { SITE } from '../constants/site';
export function Hero() {
  return <section id="top" className="relative w-full overflow-hidden bg-nfblue-dark" data-id="element-159" data-mp="9luxafjjzvqk">
      <div className="absolute inset-0" data-id="element-160" data-mp="4mi319qcrmdy">
        <img src={SITE.heroImageUrl} alt="Aerial view of a lush North Texas lawn with an irrigation system running at sunset" className="h-full w-full object-cover" data-id="element-161" data-mp="4njq6ckn7zc1" />
        <div className="absolute inset-0 bg-gradient-to-r from-nfblue-dark/95 via-nfblue-dark/80 to-nfblue-dark/40" data-id="element-162" data-mp="xbfmhg6avp8a" />
        <div className="absolute inset-0 bg-gradient-to-t from-nfblue-dark/90 via-transparent to-transparent" data-id="element-163" data-mp="s9q6dm480ldc" />
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:py-28 lg:px-8" data-id="element-164" data-mp="neaz5d0pxnpw">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="lg:col-span-8" data-id="element-165" data-mp="382fgbrjt9agi">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white backdrop-blur" data-id="element-166" data-mp="3o7lqjmmw8ymu">
            <MapPinIcon className="h-3.5 w-3.5" data-id="element-167" data-mp="39cpbapgo3b09" />
            Serving Garland, TX & All of North Texas
          </span>

          <h1 className="mt-6 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl" data-id="element-168" data-mp="30iaxysfp6ajs">
            The Hidden Water Leak Costing Texas Homeowners{' '}
            <span className="text-nfgreen-light" data-id="element-169" data-mp="35akitu1ygkf7">$80–$200 Extra</span> Every Month
          </h1>

          <p className="mt-5 max-w-2xl text-lg text-slate-200 sm:text-xl" data-id="element-170" data-mp="2qi0lja1hl75o">
            And the licensed fix that makes your lawn the best-looking one on the
            block. Most irrigation "repairs" are guesses by unlicensed workers using
            cheap parts. We diagnose what others can't see — and fix it permanently.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-171" data-mp="s335tfryasyl">
            <a href={SITE.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-full bg-nfgreen px-7 py-4 text-base font-bold text-white shadow-lg shadow-nfgreen/30 transition-transform hover:scale-[1.03] hover:bg-nfgreen-dark" data-id="element-172" data-mp="nat8vfsf4smt">
              <PhoneIcon className="h-5 w-5" data-id="element-173" data-mp="deyk02xlq1bl" />
              Call Now: {SITE.phone}
            </a>
            <a href={SITE.smsHref} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-7 py-4 text-base font-bold text-white backdrop-blur transition-colors hover:bg-white/20" data-id="element-174" data-mp="1b04aezhler20">
              <MessageSquareIcon className="h-5 w-5" data-id="element-175" data-mp="x1ef9pec31ga" />
              Text Us
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-semibold text-white" data-id="element-176" data-mp="3ltgltmwy8kmr">
            <span className="flex items-center gap-2" data-id="element-177" data-mp="3hr3sjxze2f6v">
              <BadgeCheckIcon className="h-5 w-5 text-nfgreen-light" data-id="element-178" data-mp="3htg5txu7c1dl" />
              License {SITE.license}
            </span>
            <span className="flex items-center gap-2" data-id="element-179" data-mp="21amuh5blvkk8">
              <ShieldCheckIcon className="h-5 w-5 text-nfgreen-light" data-id="element-180" data-mp="21b64lzv0l4np" />
              Fully Insured
            </span>
            <span className="flex items-center gap-2" data-id="element-181" data-mp="2686aoarv67et">
              <BadgeCheckIcon className="h-5 w-5 text-nfgreen-light" data-id="element-182" data-mp="2b1r7w0lmuxcy" />
              Residential & Commercial
            </span>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.15
      }} className="lg:col-span-4 lg:flex lg:items-end" data-id="element-183" data-mp="2kynbpt0l6vb6">
          <div className="w-full rounded-2xl border border-white/15 bg-white/95 p-6 shadow-2xl backdrop-blur" data-id="element-184" data-mp="2tyafumkpaf7x">
            <p className="font-display text-lg font-bold text-slate-900" data-id="element-185" data-mp="uoaktrxkz5wv">
              Professional System Diagnostic
            </p>
            <p className="mt-1 text-sm text-slate-600" data-id="element-186" data-mp="4bdx7gy3ybp5">
              We walk your property, find every hidden issue, and give you a clear, honest
              assessment of what it takes to fix it right.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-slate-700" data-id="element-187" data-mp="ea3eb7i6js4c">
              {['Water pressure diagnostics', 'Zone mapping & coverage', 'Leak detection', 'Code-compliant repairs'].map(item => <li key={item} className="flex items-center gap-2" data-id="element-188" data-mp="3hq0tjuv77q9j">
                  <BadgeCheckIcon className="h-4 w-4 shrink-0 text-nfgreen" data-id="element-189" data-mp="3ho67uec3ru4e" />
                  {item}
                </li>)}
            </ul>
            <a href={SITE.phoneHref} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-nfblue px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-nfblue-dark" data-id="element-190" data-mp="3rglptbnegipa">
              <PhoneIcon className="h-4 w-4" data-id="element-191" data-mp="2yyzjaaomo8ao" />
              Schedule a Diagnostic
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}